import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { listAllCoachUsers, markStaffExported, markStaffNotExported } from '../../api/admin';
import { SPORTS, SEASONS } from '../../constants/constants';
import Loader from '../common/Loader';
import StaffEditModal from './StaffEditModal';
import StaffPerformanceTab from './StaffPerformanceTab';
import './Staff.css';

const userTypeOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'director', label: 'Director' },
  { value: 'coach', label: 'Coach' }
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

const sportOptions = SPORTS.map(s => ({
  value: s,
  label: s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}));

const seasonOptions = SEASONS.map(s => ({ value: s.toLowerCase(), label: s }));

const ageRangeOptions = [
  { value: 'tots', label: 'Tots' },
  { value: 'mini', label: 'Mini' },
  { value: 'youth', label: 'Youth' }
];

const CSV_REQUIRED_FIELDS = ['ssn', 'firstName', 'lastName', 'birthdate', 'address1', 'city', 'state', 'zipCode', 'phoneNumber'];

function getCSVStatus(member) {
  const isComplete = CSV_REQUIRED_FIELDS.every(f => member[f] && String(member[f]).trim() !== '');
  if (!isComplete) return 'incomplete';
  if (member.lastExportedAt) return 'exported';
  return 'ready';
}

function Staff() {
  const [activeTab, setActiveTab] = useState('details');
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState('lastName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingStaff, setEditingStaff] = useState(null);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  // Filters
  const [userTypeFilter, setUserTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState({ value: 'active', label: 'Active' });

  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchStaff();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset selection when filters change
  useEffect(() => {
    setSelectedStaffIds([]);
  }, [userTypeFilter, statusFilter]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const response = await listAllCoachUsers(false); // false = include inactive
      if (response?.data) {
        setStaff(response.data);
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const filteredStaff = staff.filter(member => {
    if (userTypeFilter?.length && !userTypeFilter.some(t => t.value === member.userType)) return false;
    if (statusFilter && statusFilter.value !== member.status) return false;
    return true;
  });

  const sortedStaff = [...filteredStaff].sort((a, b) => {
    let aVal = a[sortColumn] || '';
    let bVal = b[sortColumn] || '';

    if (sortColumn === 'hourlyRate') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    }

    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const isAllSelected = sortedStaff.length > 0 && selectedStaffIds.length === sortedStaff.length;
  const isSomeSelected = selectedStaffIds.length > 0 && selectedStaffIds.length < sortedStaff.length;

  const handleSelectAll = (e) => {
    setSelectedStaffIds(e.target.checked ? sortedStaff.map(m => m.id) : []);
  };

  const handleSelectStaff = (id) => {
    setSelectedStaffIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleEditSuccess = (updatedStaff) => {
    setStaff(prev => prev.map(s => s.id === updatedStaff.id ? { ...s, ...updatedStaff } : s));
    setEditingStaff(null);
  };

  const formatUserType = (type) => {
    if (!type) return '-';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getStatusClass = (status) => {
    return status === 'active' ? 'status-active' : 'status-inactive';
  };

  const formatPhoneForExport = (phone) => {
    if (!phone) return '';
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // Format as XXX-XXX-XXXX if we have 10 digits
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    // If 11 digits starting with 1, skip the 1
    if (digits.length === 11 && digits[0] === '1') {
      return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone; // Return original if can't format
  };

  const exportToCSV = async () => {
    const selectedStaff = sortedStaff.filter(m => selectedStaffIds.includes(m.id));

    const headers = [
      'SSN/ITIN*', 'First name*', 'M.I.', 'Last name*', 'Suffix', 'Birthdate*',
      'Physical Address 1*', 'Physical Address 2', 'Physical City*', 'Physical State*', 'Physical ZIP Code*',
      'Primary Phone*', 'Email',
      'Mailing Address 1', 'Mailing Address 2', 'Mailing City', 'Mailing State', 'Mailing ZIP Code',
      'Other Phone', 'External Payroll ID'
    ];

    const rows = selectedStaff.map(member => [
      member.ssn || '',
      member.firstName || '',
      '', // M.I.
      member.lastName || '',
      '', // Suffix
      member.birthdate || '',
      member.address1 || '',
      '', // Physical Address 2
      member.city || '',
      member.state || '',
      member.zipCode || '',
      formatPhoneForExport(member.phoneNumber),
      member.email || '',
      '', // Mailing Address 1
      '', // Mailing Address 2
      '', // Mailing City
      '', // Mailing State
      '', // Mailing ZIP Code
      '', // Other Phone
      ''  // External Payroll ID
    ]);

    const escapeCSV = (value) => {
      if (value == null) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `staff-payroll-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);

    const exportedIds = selectedStaff.map(m => m.id).filter(Boolean);
    if (exportedIds.length > 0) {
      const now = new Date().toISOString();
      setStaff(prev => prev.map(m => exportedIds.includes(m.id) ? { ...m, lastExportedAt: now } : m));
      markStaffExported(exportedIds);
    }
    setSelectedStaffIds([]);
    setIsActionsOpen(false);
  };

  const markAsNotExported = async () => {
    setStaff(prev => prev.map(m => selectedStaffIds.includes(m.id) ? { ...m, lastExportedAt: null } : m));
    await markStaffNotExported(selectedStaffIds);
    setSelectedStaffIds([]);
    setIsActionsOpen(false);
  };

  return (
    <div className="staff-page">
      <div className="staff-header">
        <h1>Staff</h1>
        {activeTab === 'details' && (
          <div className="actions-dropdown" ref={dropdownRef}>
            <button
              className="export-btn"
              onClick={() => setIsActionsOpen(o => !o)}
              disabled={selectedStaffIds.length === 0}
            >
              Actions ({selectedStaffIds.length}) ▾
            </button>
            {isActionsOpen && (
              <div className="dropdown-menu">
                <button onClick={exportToCSV}>Export CSV for CalSavers</button>
                <button onClick={markAsNotExported}>Mark as Not Exported to CalSavers</button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="staff-tabs">
        <button
          className={`staff-tab${activeTab === 'details' ? ' staff-tab--active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={`staff-tab${activeTab === 'performance' ? ' staff-tab--active' : ''}`}
          onClick={() => setActiveTab('performance')}
        >
          Performance
        </button>
      </div>

      {activeTab === 'details' && (
        <>
          {loading ? (
            <Loader size={100} />
          ) : (
            <>
              <div className="filters-bar">
                <div className="filter-group">
                  <label>Type</label>
                  <Select
                    value={userTypeFilter}
                    onChange={setUserTypeFilter}
                    options={userTypeOptions}
                    isMulti
                    placeholder="All"
                    className="filter-select"
                    classNamePrefix="filter"
                  />
                </div>
                <div className="filter-group">
                  <label>Status</label>
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={statusOptions}
                    isClearable
                    placeholder="All"
                    className="filter-select"
                    classNamePrefix="filter"
                  />
                </div>
              </div>

              <div className="staff-count">
                Showing {sortedStaff.length} of {staff.length} staff members
              </div>

              <div className="staff-table-container">
                <table className="staff-table">
                  <thead>
                    <tr>
                      <th className="checkbox-col">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          ref={el => { if (el) el.indeterminate = isSomeSelected; }}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th onClick={() => handleSort('lastName')} className="sortable">
                        Name {getSortIcon('lastName')}
                      </th>
                      <th onClick={() => handleSort('coachName')} className="sortable">
                        Coach Name {getSortIcon('coachName')}
                      </th>
                      <th onClick={() => handleSort('email')} className="sortable">
                        Email {getSortIcon('email')}
                      </th>
                      <th onClick={() => handleSort('userType')} className="sortable">
                        Type {getSortIcon('userType')}
                      </th>
                      <th onClick={() => handleSort('status')} className="sortable">
                        Status {getSortIcon('status')}
                      </th>
                      <th>Calsavers Status</th>
                      <th onClick={() => handleSort('phoneNumber')} className="sortable">
                        Phone {getSortIcon('phoneNumber')}
                      </th>
                      <th onClick={() => handleSort('hourlyRate')} className="sortable">
                        Hourly Rate {getSortIcon('hourlyRate')}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStaff.map((member) => (
                      <tr key={member.id}>
                        <td className="checkbox-col">
                          <input
                            type="checkbox"
                            checked={selectedStaffIds.includes(member.id)}
                            onChange={() => handleSelectStaff(member.id)}
                          />
                        </td>
                        <td>{member.firstName} {member.lastName}</td>
                        <td>{member.coachName || '-'}</td>
                        <td>{member.email || '-'}</td>
                        <td>{formatUserType(member.userType)}</td>
                        <td>
                          <span className={`status-badge ${getStatusClass(member.status)}`}>
                            {member.status || 'active'}
                          </span>
                        </td>
                        <td>
                          <span className={`csv-status ${getCSVStatus(member)}`}>
                            {getCSVStatus(member).charAt(0).toUpperCase() + getCSVStatus(member).slice(1)}
                          </span>
                        </td>
                        <td>{member.phoneNumber || '-'}</td>
                        <td>{member.hourlyRate ? `$${Number(member.hourlyRate).toFixed(2)}` : '-'}</td>
                        <td>
                          <button
                            className="edit-btn"
                            onClick={() => setEditingStaff(member)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {editingStaff && (
                <StaffEditModal
                  staff={editingStaff}
                  sportOptions={sportOptions}
                  seasonOptions={seasonOptions}
                  ageRangeOptions={ageRangeOptions}
                  userTypeOptions={userTypeOptions}
                  statusOptions={statusOptions}
                  onClose={() => setEditingStaff(null)}
                  onSuccess={handleEditSuccess}
                />
              )}
            </>
          )}
        </>
      )}

      {activeTab === 'performance' && <StaffPerformanceTab />}
    </div>
  );
}

export default Staff;
